import * as Api from '@ViewModels';
import { AppState, UserSessionViewModelKey } from '../../../../models/AppState';
import { ResourceStatus, ResourceType } from './types';

// Helper functions for creating AI contexts from different data sources
export const createAIContextFromEmailData = (emailComposer: {
	emailMessage?: {
		subject?: string;
		content?: Api.IRawRichTextContentState;
	};
}): Api.EmailAIContext | null => {
	if (!emailComposer?.emailMessage) return null;

	return {
		_type: 'EmailAIContext',
		subject: emailComposer.emailMessage.subject || '',
		body: emailComposer.emailMessage.content || {
			blocks: [],
			entityMap: {},
		},
	} as Api.EmailAIContext;
};

export const createAIContextFromSocialData = (
	socialPost: Api.SocialMediaPostViewModel
): Api.SocialMediaAIContext | null => {
	if (!socialPost?.content) return null;

	return {
		_type: 'SocialMediaAIContext',
		content: socialPost.content,
	} as Api.SocialMediaAIContext;
};

export const createAIContextFromBlogData = (
	title: string,
	_content: Api.IRawRichTextContentState
): Api.BlogAIContext | null => {
	return {
		type: 'BlogAIContext',
		title: title || 'Blog Article',
		content: _content || {
			blocks: [],
			entityMap: {},
		},
	} as Api.BlogAIContext;
};

export interface CampaignResource {
	type: ResourceType;
	content: Api.AIContext;
	scheduledDate?: Date;
	isCompleted: boolean;
}

export interface CampaignSession {
	id: string;
	primaryResource: CampaignResource;
	resources: Record<ResourceType, CampaignResource | null>;
	createdAt: Date;
	lastUpdatedAt: Date;
}

type StoreListener = () => void;

class CampaignSessionStore {
	private static readonly STORAGE_KEY = 'levitate_campaign_session';
	private static readonly SESSION_DURATION_MINUTES = 10;

	currentSession: CampaignSession | null = null;
	shouldOpenAIDrawer = false;
	aiSourceContent: Api.AIContext | null = null;
	aiSourceType: string | null = null;

	private listeners = new Set<StoreListener>();

	constructor() {
		this.loadFromStorage();
	}

	subscribe(listener: StoreListener): () => void {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	private notifyListeners(): void {
		this.listeners.forEach(listener => listener());
	}

	get hasActiveSession(): boolean {
		return this.currentSession !== null;
	}

	get incompleteResources(): ResourceType[] {
		if (!this.currentSession) return [];

		const incomplete: ResourceType[] = [];
		const allTypes: ResourceType[] = ['email', 'social', 'blog'];

		for (const type of allTypes) {
			const res = this.currentSession.resources[type];
			if (!res || !res.isCompleted) {
				incomplete.push(type);
			}
		}

		return incomplete;
	}

	get completedResources(): CampaignResource[] {
		if (!this.currentSession) return [];
		return Object.values(this.currentSession.resources).filter(resource => resource !== null) as CampaignResource[];
	}

	get secondaryResources(): ResourceStatus[] {
		if (!this.currentSession) return [];

		const allTypes: ResourceType[] = ['email', 'social', 'blog'];
		const primaryType = this.currentSession.primaryResource.type;

		const userSession = AppState[UserSessionViewModelKey];

		return allTypes
			.filter(type => type !== primaryType)
			.filter(type => {
				if (type === 'email') return true;

				if (type === 'social') {
					return userSession?.account?.features?.socialMedia?.enabled ?? false;
				}

				if (type === 'blog') {
					return userSession?.account?.features?.blogFeature?.enabled ?? false;
				}

				return true;
			})
			.map(type => this.getResourceStatus(type));
	}

	startSession(primaryType: ResourceType, content: Api.AIContext, scheduledDate?: Date): string {
		const sessionId = this.generateSessionId();
		const now = new Date();

		const primaryResource: CampaignResource = {
			type: primaryType,
			content,
			isCompleted: true,
			scheduledDate,
		};

		this.currentSession = {
			id: sessionId,
			primaryResource,
			resources: {
				email: primaryType === 'email' ? primaryResource : null,
				social: primaryType === 'social' ? primaryResource : null,
				blog: primaryType === 'blog' ? primaryResource : null,
			},
			createdAt: now,
			lastUpdatedAt: now,
		};

		this.saveToStorage();
		this.notifyListeners();
		return sessionId;
	}

	updateSession(resourceType: ResourceType, content: Api.AIContext, scheduledDate?: Date): CampaignSession | null {
		if (!this.currentSession) return null;

		const resource: CampaignResource = {
			type: resourceType,
			content,
			isCompleted: true,
			scheduledDate,
		};

		this.currentSession.resources[resourceType] = resource;

		if (this.currentSession.primaryResource.type === resourceType) {
			this.currentSession.primaryResource = resource;
		}

		this.currentSession.lastUpdatedAt = new Date();

		this.saveToStorage();
		this.notifyListeners();
		return this.currentSession;
	}

	getResourceStatus(type: ResourceType): ResourceStatus {
		if (!this.currentSession) {
			return {
				type,
				isCompleted: false,
			};
		}

		const resource = this.currentSession.resources[type];
		if (!resource) {
			return {
				type,
				isCompleted: false,
			};
		}

		return {
			type: resource.type,
			isCompleted: resource.isCompleted,
			scheduledDate: resource.scheduledDate,
		};
	}

	clearSession(): void {
		this.currentSession = null;
		this.shouldOpenAIDrawer = false;
		this.clearAISourceData();
		localStorage.removeItem(CampaignSessionStore.STORAGE_KEY);
		this.notifyListeners();
	}

	setShouldOpenAIDrawer(value: boolean): void {
		this.shouldOpenAIDrawer = value;
		this.notifyListeners();
	}

	setAISourceData(sourceContent: Api.AIContext | null, sourceType: string | null, shouldOpenDrawer = true): void {
		this.aiSourceContent = sourceContent;
		this.aiSourceType = sourceType;
		this.shouldOpenAIDrawer = shouldOpenDrawer;
		this.notifyListeners();
	}

	clearAISourceData(): void {
		this.aiSourceContent = null;
		this.aiSourceType = null;
		this.shouldOpenAIDrawer = false;
		this.notifyListeners();
	}

	clearExpiredSessions(): void {
		if (!this.currentSession) {
			this.clearSession();
			return;
		}

		try {
			const now = new Date();
			const sessionAge = now.getTime() - new Date(this.currentSession.createdAt).getTime();
			const maxAge = CampaignSessionStore.SESSION_DURATION_MINUTES * 60 * 1000; // Convert to milliseconds

			if (sessionAge > maxAge) {
				console.debug(`Clearing expired campaign session (age: ${Math.round(sessionAge / (60 * 1000))} minutes)`);
				this.clearSession();
				// Note: clearSession() already calls notifyListeners()
			}
		} catch (error) {
			console.error('Error checking session expiration:', error);
			this.clearSession();
			// Note: clearSession() already calls notifyListeners()
		}
	}

	private loadFromStorage(): void {
		try {
			const stored = localStorage.getItem(CampaignSessionStore.STORAGE_KEY);
			if (!stored) return;

			const session: CampaignSession = JSON.parse(stored);

			if (!session.id || !session.createdAt || !session.primaryResource) {
				console.warn('Invalid session structure found in localStorage, clearing...');
				this.clearSession();
				return;
			}

			session.createdAt = new Date(session.createdAt);
			session.lastUpdatedAt = new Date(session.lastUpdatedAt);
			if (session.primaryResource.scheduledDate) {
				session.primaryResource.scheduledDate = new Date(session.primaryResource.scheduledDate);
			}

			Object.values(session.resources).forEach(resource => {
				if (resource?.scheduledDate) {
					resource.scheduledDate = new Date(resource.scheduledDate);
				}
			});

			const now = new Date();
			const sessionAge = now.getTime() - session.createdAt.getTime();
			const maxAge = CampaignSessionStore.SESSION_DURATION_MINUTES * 60 * 1000;

			if (sessionAge > maxAge) {
				console.debug(`Session expired during load (age: ${Math.round(sessionAge / (60 * 1000))} minutes)`);
				this.clearSession();
				return;
			}

			this.currentSession = session;
		} catch (error) {
			console.error('Error loading campaign session:', error);
			this.clearSession();
		}
	}

	private saveToStorage(): void {
		try {
			if (this.currentSession) {
				const serialized = JSON.stringify(this.currentSession);
				localStorage.setItem(CampaignSessionStore.STORAGE_KEY, serialized);
			} else {
				localStorage.removeItem(CampaignSessionStore.STORAGE_KEY);
			}
		} catch (error) {
			console.error('Error saving campaign session:', error);
			if ((error as Error).name === 'QuotaExceededError') {
				console.warn('localStorage quota exceeded, clearing campaign session');
				this.clearSession();
			}
		}
	}

	private generateSessionId(): string {
		return `campaign_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
	}

	getPrimaryResourceContent(): Api.AIContext | null {
		return this.currentSession?.primaryResource.content || null;
	}

	getSession(sessionId?: string): CampaignSession | null {
		if (!this.currentSession) return null;

		if (sessionId && this.currentSession.id !== sessionId) {
			return null;
		}

		return this.currentSession;
	}
}

export const campaignSessionStore = new CampaignSessionStore();

export const shouldOpenAIDrawer = () => campaignSessionStore.shouldOpenAIDrawer;

export const clearGlobalAISourceData = () => campaignSessionStore.clearAISourceData();
